import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ArenaPostOption, fetchTeamPosts } from '../../../api/group/arena.api';
import { postService } from '../../../services/post.service';
import { extractPostId, formatVn, isFullPostId } from '../arena-format';

interface Props {
  memberIds: string[];
  startAt: string;
  busy: boolean;
  onPick: (postId: string) => void;
  onClose: () => void;
}

const ArenaPostPicker: React.FC<Props> = ({ memberIds, startAt, busy, onPick, onClose }) => {
  const [posts, setPosts] = useState<ArenaPostOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [link, setLink] = useState('');
  const [resolving, setResolving] = useState(false);

  // Đường dẫn chia sẻ chứa mã ngắn, backend cần mã đầy đủ nên tra lại trước khi nộp.
  const submitLink = async () => {
    const raw = extractPostId(link);
    if (!raw) return;
    if (isFullPostId(raw)) {
      onPick(raw);
      return;
    }
    setResolving(true);
    try {
      const post = await postService.getPostByShortId(raw);
      if (!post?.id) throw new Error('not found');
      onPick(String(post.id));
    } catch {
      toast.error('Không tìm thấy bài viết từ đường dẫn này.');
    } finally {
      setResolving(false);
    }
  };

  useEffect(() => {
    fetchTeamPosts(memberIds, startAt)
      .then(setPosts)
      .catch((e: any) => toast.error(e?.message || 'Không tải được danh sách bài'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col gap-3 rounded-[16px] border border-[#DEE0E6] p-4 dark:border-slate-700">
      <div className="flex items-center justify-between">
        <h3 className="text-[16px] font-semibold text-[#19052f] dark:text-slate-100">Chọn bài dự thi</h3>
        <button type="button" className="text-[13px] text-[#657384] dark:text-slate-400" onClick={onClose}>
          Đóng
        </button>
      </div>
      <p className="text-[13px] text-[#657384] dark:text-slate-400">
        Chỉ hiện bài của thành viên đội đăng sau giờ bắt đầu thử thách.
      </p>
      {loading ? (
        <div className="text-[13px] text-[#657384] dark:text-slate-400">Đang tải bài viết...</div>
      ) : posts.length === 0 ? (
        <div className="text-[13px] text-[#657384] dark:text-slate-400">Chưa có bài nào hợp lệ. Bạn có thể dán đường dẫn bài bên dưới.</div>
      ) : (
        <ul className="flex max-h-[260px] flex-col gap-2 overflow-y-auto">
          {posts.map((p) => (
            <li
              key={p.id}
              className="flex items-center justify-between gap-3 rounded-[12px] border border-[#DEE0E6] px-3 py-2 dark:border-slate-700"
            >
              <div className="min-w-0">
                <div className="truncate text-[14px] font-medium text-[#19052f] dark:text-slate-100">{p.title}</div>
                <div className="text-[12px] text-[#657384] dark:text-slate-400">
                  {p.authorName} lúc {formatVn(p.createdAt)}
                </div>
              </div>
              <button
                type="button"
                disabled={busy}
                className="shrink-0 rounded-full bg-[#066bff] px-3 py-1 text-[13px] text-white disabled:opacity-60"
                onClick={() => onPick(p.id)}
              >
                Chọn bài này
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex gap-2">
        <input
          value={link}
          onChange={(e) => setLink(e.target.value)}
          placeholder="Dán đường dẫn hoặc mã bài"
          className="min-w-0 flex-1 rounded-full border border-[#DEE0E6] px-4 py-2 text-[14px] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
        />
        <button
          type="button"
          disabled={busy || resolving || !extractPostId(link)}
          className="rounded-full border border-[#066bff] px-4 py-2 text-[14px] text-[#066bff] disabled:opacity-60"
          onClick={submitLink}
        >
          Nộp bài này
        </button>
      </div>
    </div>
  );
};

export default ArenaPostPicker;
